import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { cloneDeep, isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/DataContext';
import { db } from '../firebase';


const sanitizeString = (str) => {
    if (typeof str !== 'string') return '';
    return str
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ''); // Removes all non-alphanumeric characters except spaces
};

const usePaginatedRegistrants = (pageSize = 10, collectionName = "Registrants", orderData = "createdAt", searchTerm = "", searchTeacherName = "", ageCategoryFilter = null, performanceCategoryFilter = null, competitionCategoryFilter = null, eventIdFilter = null, invoiceStatusFilter = null) => {
    const { loggedInUser } = useAuth();

    // State to hold all documents fetched from Firestore
    const [allData, setAllData] = useState([]);
    // State to hold the final, displayed data (after filtering and pagination)
    const [registrantDatas, setRegistrantDatas] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalDocs, setTotalDocs] = useState(0);

    // This function now only fetches ALL data from Firestore ONCE.
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const q = query(collection(db, collectionName), orderBy(orderData));
            const querySnapshot = await getDocs(q);
            const allDocs = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

            // Perform the 'paymentStatus' mapping once on the full dataset
            const processedData = allDocs.map((eachData) => {
                const tempData = cloneDeep(eachData);
                if (!tempData.paymentStatus) {
                    tempData.paymentStatus = "PAID";
                }
                return tempData;
            });

            setAllData(processedData);
        } catch (err) {
            console.error("Error getting documents: ", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [collectionName, orderData]);

    // Initial data fetch
    useEffect(() => {
        if (!isEmpty(loggedInUser.token)) {
            fetchData();
        }
    }, [loggedInUser.token, fetchData]);

    // This new useEffect handles filtering and pagination whenever the search term or page changes.
    useEffect(() => {
        // Start with the full, original dataset
        let filteredData = allData;

        // Apply search filter if a search term exists
        if (searchTerm && allData.length > 0) {
            const lowercasedFilter = searchTerm.toLowerCase();
            filteredData = allData.filter(registrant => {
                // Check each performer in the performers array
                return registrant.performers?.some(performer => {
                    const fullName = performer.fullName ? `${performer.fullName}`.toLowerCase() : `${performer.firstName || ''} ${performer.lastName || ''}`.toLowerCase();
                    return fullName.includes(lowercasedFilter);
                });
            });
        }

        if (searchTeacherName && allData.length > 0) {
            const lowercasedFilter = searchTeacherName.toLowerCase();
            const sanitizedFilter = sanitizeString(lowercasedFilter);

            filteredData = allData.filter(registrant => {

                // 1. Check if the teacher's name matches
                const teacherNameMatch = (registrant.teacherName || '').toLowerCase().includes(sanitizedFilter);
                if (teacherNameMatch) {
                    return true;
                }

                // 2. If not, check if any of the performers' names match
                const nameMatch = (registrant.name || '').toLowerCase().includes(sanitizedFilter);
                if (nameMatch) {
                    return true;
                }
            });
        }

        // --- NEW: Apply Age Category Filter ---
        if (ageCategoryFilter && allData.length > 0) {
            filteredData = filteredData.filter(registrant =>
                registrant.ageCategory === ageCategoryFilter
            );
        }

        if (performanceCategoryFilter && allData.length > 0) {
            // Sanitize the filter value once before the loop for efficiency
            const sanitizedFilter = performanceCategoryFilter.toLowerCase().trim();

            filteredData = filteredData.filter(registrant => {
                // Sanitize the data from your database before comparing
                const category = (registrant.PerformanceCategory || '').toLowerCase().trim();
                return category === sanitizedFilter;
            });
        }

        if (competitionCategoryFilter && allData.length > 0) {
            // Sanitize the filter value once before the loop for efficiency
            const sanitizedFilter = competitionCategoryFilter.toLowerCase().trim();

            filteredData = filteredData.filter(registrant => {
                // Sanitize the data from your database before comparing
                const category = (registrant.competitionCategory || '').toLowerCase().trim();
                return category === sanitizedFilter;
            });
        }

        // --- NEW: Apply Event ID Filter ---
        if (eventIdFilter && allData.length > 0) {
            filteredData = filteredData.filter(registrant =>
                registrant.eventId === eventIdFilter
            );
        }

        // --- NEW: Apply Invoice Status Filter ---
        if (invoiceStatusFilter && allData.length > 0) {
            filteredData = filteredData.filter(registrant =>
                registrant.invoiceStatus === invoiceStatusFilter
            );
        }

        // Update the total documents count based on the filtered results
        setTotalDocs(filteredData.length);

        // Apply pagination to the filtered data
        const offset = (page - 1) * pageSize;
        const paginatedData = filteredData.slice(offset, offset + pageSize);

        setRegistrantDatas(paginatedData);

    }, [searchTerm, searchTeacherName, page, allData, pageSize, ageCategoryFilter, performanceCategoryFilter, competitionCategoryFilter, eventIdFilter, invoiceStatusFilter]);

    const totalPages = Math.ceil(totalDocs / pageSize);

    return { allData, registrantDatas, loading, error, page, setPage, totalPages, totalDocs, fetchData };
};

export default usePaginatedRegistrants;